/**
 * Portfolio orchestration logic
 * Handles coordination between portfolio state, views, and actions
 */

import { useCallback } from 'react';

/**
 * Custom hook for orchestrating portfolio operations
 * @param {Object} portfolioHooks - Portfolio-related hooks
 * @param {Object} cleanupUtils - Cleanup utility functions
 * @returns {Object} Enhanced portfolio operations
 */
const usePortfolioOrchestrator = (portfolioHooks, cleanupUtils) => {
  const {
    // Portfolio state
    pinnedClients,
    orderedPinnedClients,
    isPinned,
    togglePinClient,
    unpinClient,
    unpinAllClients,
    setPinnedClients,
    setOrderedPinnedClients,
    
    // Portfolio views
    setPortfolioType,
    setActiveManager,
    setIsHardPinned,
    setHardPinnedClients,
    pinManagerClients,
    
    // Portfolio actions
    unpinCountryClients
  } = portfolioHooks;

  const {
    cleanupClientInteractions,
    cleanupCountryInteractions,
    resetAllInteractions
  } = cleanupUtils;

  /**
   * Enhanced toggle pin client with state cleanup
   * @param {Object} client - Client to toggle
   */
  const enhancedTogglePinClient = useCallback((client) => {
    const wasPinned = isPinned(client);
    togglePinClient(client);
    
    if (wasPinned) {
      // Check if this was the last client
      const newPinnedClients = pinnedClients.filter(
        pinned => !(pinned.client_name === client.client_name && pinned.country === client.country)
      );
      
      // Reset portfolio type if no clients remain
      if (newPinnedClients.length === 0) {
        setPortfolioType('custom');
        setActiveManager('');
      }

      // Clean up interaction states
      cleanupClientInteractions(client);
    }
  }, [isPinned, togglePinClient, pinnedClients, setPortfolioType, setActiveManager, cleanupClientInteractions]);

  /**
   * Enhanced unpin client with state cleanup
   * @param {Object} client - Client to unpin
   */
  const enhancedUnpinClient = useCallback((client) => {
    unpinClient(client);
    cleanupClientInteractions(client);
  }, [unpinClient, cleanupClientInteractions]);

  /**
   * Enhanced unpin all clients with locked client preservation
   */
  const enhancedUnpinAllClients = useCallback(() => {
    const { lockedClients, getClientKey } = cleanupUtils;
    
    // Get currently locked clients before clearing
    const lockedClientKeys = new Set(lockedClients);
    const lockedClientsList = pinnedClients.filter(client => 
      lockedClientKeys.has(getClientKey(client))
    );
    
    // Clear all clients first
    unpinAllClients();
    
    // Re-add locked clients to maintain them in the list
    if (lockedClientsList.length > 0) {
      setPinnedClients(lockedClientsList);
      setOrderedPinnedClients(lockedClientsList);
    } else {
      // Only reset portfolio type if no locked clients remain
      setPortfolioType('custom');
      setActiveManager('');
    }
    
    // Reset portfolio state
    setIsHardPinned(false);
    setHardPinnedClients([]);
    
    // Reset interaction states (but preserve locked clients)
    resetAllInteractions();
  }, [
    pinnedClients, unpinAllClients, setPinnedClients, setOrderedPinnedClients,
    setPortfolioType, setActiveManager, setIsHardPinned, setHardPinnedClients,
    resetAllInteractions, cleanupUtils
  ]);

  /**
   * Enhanced pin manager clients with locked client preservation
   * @param {string} manager - Manager name
   * @param {Array} clientData - Available client data
   */
  const enhancedPinManagerClients = useCallback((manager, clientData) => {
    const { lockedClients, getClientKey } = cleanupUtils;
    pinManagerClients(
      manager, 
      clientData, 
      pinnedClients, 
      orderedPinnedClients, 
      setPinnedClients, 
      setOrderedPinnedClients, 
      lockedClients, 
      getClientKey
    );
  }, [pinManagerClients, pinnedClients, orderedPinnedClients, setPinnedClients, setOrderedPinnedClients, cleanupUtils]);

  /**
   * Enhanced unpin country clients with state cleanup
   * @param {string} countryName - Country to unpin
   * @returns {number} Number of clients removed
   */
  const enhancedUnpinCountryClients = useCallback((countryName) => {
    const removedCount = unpinCountryClients(countryName);
    
    // Clean up interaction states for this country
    cleanupCountryInteractions(pinnedClients, countryName);
    
    // Reset portfolio type if no clients left
    const remainingClients = pinnedClients.filter(client => client.country !== countryName);
    if (remainingClients.length === 0) {
      setPortfolioType('custom');
      setActiveManager('');
    }
    
    return removedCount;
  }, [unpinCountryClients, cleanupCountryInteractions, pinnedClients, setPortfolioType, setActiveManager]);

  return {
    enhancedTogglePinClient,
    enhancedUnpinClient,
    enhancedUnpinAllClients,
    enhancedPinManagerClients,
    enhancedUnpinCountryClients
  };
};

export default usePortfolioOrchestrator;
